/**
 * Transcript downloaden (wiki-feedback Meeting notes, 9 sep 2026): als platte
 * tekst of als Word-document. Voor .docx bouwen we het minimale OOXML-pakket
 * zelf met jszip (zit al in de bundel); één alinea per regel, sprekers vet.
 */
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const xmlVeilig = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Bestandsnaam zonder rare tekens: "2026-09-08 Workshop interne communicatie". */
export function bestandsnaamVoor(meeting, ext) {
  const datum = String(meeting?.meeting_date || '').slice(0, 10) || 'meeting';
  const titel = String(meeting?.title || 'transcript').replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
  return `${datum} ${titel}.${ext}`;
}

/** Markdown-transcript → platte tekst: **[Spreker]:** wordt "Spreker:". */
export function naarPlatteTekst(transcript) {
  return String(transcript || '')
    .replace(/\*\*\[([^\]]+)\]:\*\*/g, '$1:')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Eén Word-alinea; een sprekerlabel aan het begin van de regel wordt vet. */
function alinea(regel) {
  const m = /^\*\*\[([^\]]+)\]:\*\*\s*(.*)$/.exec(regel) || /^\*\*([^*]+):\*\*\s*(.*)$/.exec(regel);
  if (m) {
    return `<w:p><w:r><w:rPr><w:b/></w:rPr><w:t xml:space="preserve">${xmlVeilig(m[1])}: </w:t></w:r><w:r><w:t xml:space="preserve">${xmlVeilig(m[2])}</w:t></w:r></w:p>`;
  }
  return `<w:p><w:r><w:t xml:space="preserve">${xmlVeilig(regel.replace(/\*\*/g, ''))}</w:t></w:r></w:p>`;
}

export function bouwDocxXml({ titel, datum, deelnemers, samenvatting, transcript }) {
  const kop = `<w:p><w:r><w:rPr><w:b/><w:sz w:val="32"/></w:rPr><w:t xml:space="preserve">${xmlVeilig(titel || 'Transcript')}</w:t></w:r></w:p>`;
  const meta = `<w:p><w:r><w:rPr><w:color w:val="666666"/></w:rPr><w:t xml:space="preserve">${xmlVeilig([datum, deelnemers].filter(Boolean).join(' · '))}</w:t></w:r></w:p>`;
  const samen = samenvatting
    ? `<w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Samenvatting</w:t></w:r></w:p>${String(samenvatting).split('\n').filter((r) => r.trim()).map(alinea).join('')}<w:p/>`
    : '';
  const body = `<w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Transcript</w:t></w:r></w:p>${String(transcript || '').split('\n').filter((r) => r.trim()).map(alinea).join('')}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${kop}${meta}<w:p/>${samen}${body}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134"/></w:sectPr></w:body></w:document>`;
}

export async function maakDocxBlob(meeting, { samenvatting = '', transcript = '' } = {}) {
  const zip = new JSZip();
  zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`);
  zip.file('_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`);
  zip.file('word/document.xml', bouwDocxXml({
    titel: meeting?.title, datum: String(meeting?.meeting_date || '').slice(0, 10),
    deelnemers: meeting?.participants, samenvatting, transcript,
  }));
  return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
}

export async function downloadTranscript(meeting, { formaat = 'txt', samenvatting = '', transcript = '' } = {}) {
  if (formaat === 'docx') {
    saveAs(await maakDocxBlob(meeting, { samenvatting, transcript }), bestandsnaamVoor(meeting, 'docx'));
    return;
  }
  const kop = `${meeting?.title || 'Transcript'}\n${[String(meeting?.meeting_date || '').slice(0, 10), meeting?.participants].filter(Boolean).join(' · ')}\n\n`;
  const inhoud = `${kop}${samenvatting ? `SAMENVATTING\n${naarPlatteTekst(samenvatting)}\n\nTRANSCRIPT\n` : ''}${naarPlatteTekst(transcript)}\n`;
  saveAs(new Blob([inhoud], { type: 'text/plain;charset=utf-8' }), bestandsnaamVoor(meeting, 'txt'));
}
